import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Star, X, ExternalLink, Gift, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

/**
 * Platform Review Prompt Modal
 * Shows to new users after signup to encourage them to leave a 5-star review
 * on Google, App Store, Trustpilot, etc.
 *
 * Phase 7g.15.98 (Rick QA 2026-06-09) — P0 customer blocking fix:
 *   Rick's wife was BLOCKED on first login because the previous modal
 *   pushed "Maybe later" behind the customer-portal bottom nav at
 *   390×844 phone vertical AND the close X was only 32×32 (below
 *   touch-target standard) and visually obscured by the gradient
 *   header title. Fixed below:
 *     1. Top-of-modal sticky "Skip for now" button — 44×44 minimum,
 *        primary visual weight, ALWAYS visible regardless of scroll.
 *     2. Close X enlarged to 44×44 (min-h-11 min-w-11) and pulled out
 *        of the gradient header into a distinct sticky bar.
 *     3. Modal body now `max-h-[88vh] overflow-y-auto` so long content
 *        scrolls inside the modal instead of being clipped by the
 *        bottom nav / viewport.
 *     4. Backdrop click DISMISSES the modal (with the same 7-day
 *        cooldown persistence as the X / Skip button).
 *     5. Modal container respects bottom safe-area via `pb-safe-bottom`
 *        on the body padding so the last button never hides behind
 *        the customer bottom-nav (h-14).
 *     6. Maybe later button preserved at the bottom as a SECONDARY
 *        dismiss for users who skim the platforms first.
 *     7. data-testids added: `platform-review-skip-top`,
 *        `platform-review-backdrop`, `platform-review-maybe-later`.
 */
export const PlatformReviewPrompt = ({ 
  isOpen, 
  onClose,
  customerName = "there"
}) => {
  const { t } = useTranslation();
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  if (!isOpen) return null;

  // Phase 7g.15.98 — single persist-and-close helper so EVERY dismiss
  // path (X, Skip for now, Maybe later, backdrop click, Esc) writes
  // the same cooldown localStorage and calls onClose. Prevents the
  // "modal pops again right after dismiss" symptom Rick's wife hit.
  const persistAndClose = (reason = "later") => {
    try {
      localStorage.setItem("washpros_review_prompted", reason);
      localStorage.setItem(
        "washpros_review_prompted_date",
        new Date().toISOString()
      );
    } catch (_e) { /* ignore quota errors */ }
    onClose();
  };

  const reviewPlatforms = [
    {
      id: "google",
      name: "Google",
      icon: "G",
      color: "bg-blue-500 hover:bg-blue-600",
      url: "https://g.page/r/washpros/review",
      description: t("platform_review.desc_google"),
    },
    {
      id: "appstore",
      name: "App Store",
      icon: "🍎",
      color: "bg-slate-800 hover:bg-slate-700",
      url: "https://apps.apple.com/app/washpros/reviews",
      description: t("platform_review.desc_appstore"),
    },
    {
      id: "trustpilot",
      name: "Trustpilot",
      icon: "✓",
      color: "bg-emerald-600 hover:bg-emerald-700",
      url: "https://trustpilot.com/review/washpros.io",
      description: t("platform_review.desc_trustpilot"),
    },
    {
      id: "facebook",
      name: "Facebook",
      icon: "f",
      color: "bg-blue-600 hover:bg-blue-700",
      url: "https://facebook.com/washpros",
      description: t("platform_review.desc_facebook"),
    }
  ];

  const handlePlatformClick = (platform) => {
    setSelectedPlatform(platform.id);
    // Open review URL in new tab — main app surface remains usable
    // even if the platform tab fails to load. The modal also closes
    // immediately so the user is never trapped.
    window.open(platform.url, "_blank", "noopener,noreferrer");
    persistAndClose("true");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-sm"
      data-testid="platform-review-backdrop"
      onClick={() => persistAndClose("later")}
    >
      <Card
        className="w-full max-w-md max-h-[88vh] bg-gradient-to-br from-slate-900 to-slate-800 border-amber-500/30 animate-in zoom-in-95 shadow-2xl shadow-amber-500/10 flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="platform-review-prompt-modal"
      >
        <CardContent className="p-0 flex flex-col min-h-0 flex-1">
          {/* Phase 7g.15.98 — sticky top dismiss bar. ALWAYS visible
              regardless of scroll position. Two affordances: a large
              44×44 X on the right AND a clearly-labelled "Skip for
              now" pill on the left — covers both the iconographic
              and the lexical mental model. */}
          <div className="sticky top-0 z-10 flex items-center justify-between gap-2 px-3 py-2 bg-slate-900/95 backdrop-blur-md border-b border-white/5">
            <button
              onClick={() => persistAndClose("later")}
              data-testid="platform-review-skip-top"
              className="min-h-11 px-4 inline-flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-slate-200 text-sm font-medium transition-colors"
            >
              Skip for now
            </button>
            <button
              onClick={() => persistAndClose("later")}
              data-testid="platform-review-prompt-close-btn"
              aria-label={t("platform_review.close_aria")}
              className="min-h-11 min-w-11 inline-flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-slate-200 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Header with gradient — title only, no close button (close
              is in the sticky bar above). */}
          <div className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 p-5 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgZmlsbD0iI2ZmZiIgZmlsbC1vcGFjaXR5PSIwLjEiIGN4PSIyMCIgY3k9IjIwIiByPSIzIi8+PC9nPjwvc3ZnPg==')] opacity-30"></div>

            {/* Stars animation */}
            <div className="flex justify-center gap-1 mb-2">
              {[1, 2, 3, 4, 5].map((star, i) => (
                <Star
                  key={star}
                  className="w-7 h-7 text-white fill-white animate-pulse"
                  style={{ animationDelay: `${i * 0.1}s` }}
                />
              ))}
            </div>

            <h2 className="text-xl sm:text-2xl font-bold text-white mb-1 relative z-10">
              Loving WashPros?
            </h2>
            <p className="text-white/90 text-xs sm:text-sm relative z-10">
              Your review helps us grow!
            </p>
          </div>

          {/* Scrollable content — pb-20 leaves room above the customer
              bottom-nav (h-14) on phone vertical so the secondary
              "Maybe later" link is never hidden behind it. */}
          <div className="flex-1 overflow-y-auto p-5 pb-20">
            <p className="text-slate-300 text-center text-sm mb-5">
              Hi {customerName}! We&apos;d be incredibly grateful if you could share your first impressions of WashPros.
            </p>

            {/* Review Platform Buttons */}
            <div className="space-y-2.5 mb-5">
              {reviewPlatforms.map((platform) => (
                <button
                  key={platform.id}
                  onClick={() => handlePlatformClick(platform)}
                  data-testid={`platform-review-${platform.id}`}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl ${platform.color} text-white transition-all transform hover:scale-[1.02] active:scale-[0.98]`}
                >
                  <span className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-lg font-bold flex-shrink-0">
                    {platform.icon}
                  </span>
                  <div className="flex-1 text-left min-w-0">
                    <p className="font-semibold text-sm">Review on {platform.name}</p>
                    <p className="text-white/70 text-[11px] break-words">{platform.description}</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-white/70 flex-shrink-0" />
                </button>
              ))}
            </div>

            {/* Incentive Box */}
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-3 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-purple-500/30 flex items-center justify-center flex-shrink-0">
                  <Gift className="w-4 h-4 text-purple-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-purple-300 font-semibold text-xs">Thank You Bonus!</p>
                  <p className="text-slate-400 text-[11px] break-words">
                    Leave a review and email us a screenshot at{" "}
                    <span className="text-purple-400">hello@washpros.io</span> to receive <span className="text-white font-semibold">$5 credit</span> on your first wash!
                  </p>
                </div>
              </div>
            </div>

            {/* Maybe Later — secondary dismiss kept for users who
                scrolled past the platforms before deciding. */}
            <button
              onClick={() => persistAndClose("later")}
              data-testid="platform-review-maybe-later"
              className="w-full min-h-11 text-slate-400 text-sm hover:text-white transition-colors py-2 rounded-lg hover:bg-white/5"
            >
              Maybe later
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Hook to manage platform review prompt visibility
 * Shows the prompt to new users after a delay
 */
export const usePlatformReviewPrompt = (isNewUser = false, delayMs = 5000) => {
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Check if user has already been prompted
    const prompted = localStorage.getItem("washpros_review_prompted");
    const promptedDate = localStorage.getItem("washpros_review_prompted_date");
    
    // If already reviewed, don't show
    if (prompted === "true") {
      return;
    }
    
    // If said "later", wait at least 7 days before showing again
    if (prompted === "later" && promptedDate) {
      const daysSincePrompt = (Date.now() - new Date(promptedDate).getTime()) / (1000 * 60 * 60 * 24);
      if (daysSincePrompt < 7) {
        return;
      }
    }
    
    // Show prompt after delay for new users
    if (isNewUser) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, delayMs);
      
      return () => clearTimeout(timer);
    }
  }, [isNewUser, delayMs]);

  const closePrompt = () => {
    setShowPrompt(false);
  };

  return { showPrompt, closePrompt };
};

/**
 * Floating review reminder that appears after first login
 */
export const ReviewReminderBanner = ({ onReviewClick }) => {
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Check if should show
    const prompted = localStorage.getItem("washpros_review_prompted");
    const promptedDate = localStorage.getItem("washpros_review_prompted_date");
    const signupDate = localStorage.getItem("washpros_signup_date");
    
    // Don't show if already reviewed
    if (prompted === "true") {
      return;
    }
    
    // Show banner if user signed up but hasn't reviewed yet
    if (signupDate && !prompted) {
      const daysSinceSignup = (Date.now() - new Date(signupDate).getTime()) / (1000 * 60 * 60 * 24);
      // Show after 1 day
      if (daysSinceSignup >= 1) {
        setVisible(true);
      }
    }
  }, []);

  if (!visible || dismissed) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-40 animate-in slide-in-from-bottom">
      <Card className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border-amber-500/30 shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center flex-shrink-0 shadow-lg">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm">Enjoying WashPros?</p>
              <p className="text-slate-400 text-xs">
                A quick 5-star review helps us reach more car owners! ⭐
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={onReviewClick}
                className="bg-amber-500 hover:bg-amber-600 text-black text-sm px-4 py-2 font-semibold"
              >
                Review
              </Button>
              <button
                onClick={() => setDismissed(true)}
                className="p-2 text-slate-500 hover:text-white rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlatformReviewPrompt;
